import classNames from 'classnames';
import { useI18n } from '@/libraries/contexts/i18n-context';
import { useEffect, useRef, useState } from 'react';
import React from 'react';
import Typography from '@/components/atoms/typography/typography';
import useCurrencyFormatter from '@/libraries/formatters/use-currency-formatter';
import styles from './add-on-products-matrix.module.scss';

export interface AddOnProductsMatrixProps {
  /** The class name to apply */
  className?: string;
  /** The text to show as headlines */
  headlines: string[];
  /** The data to show in the table */
  items: string[][];
  /** Indicates whether to show the price per person labels */
  showPricePerPerson?: boolean;
}

const AddOnProductsMatrix = ({
  className,
  headlines = [],
  items = [],
  showPricePerPerson = false,
}: AddOnProductsMatrixProps) => {
  const { t } = useI18n();
  const tableRef = useRef<HTMLTableElement>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [hasHorizontalOverflow, setHasHorizontalOverflow] = useState(false);
  const [hasVerticalOverflow, setHasVerticalOverflow] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const currencyFormatter = useCurrencyFormatter();

  useEffect(() => {
    if (!tableRef.current) return;

    if (!tableWrapperRef.current) return;

    const isOverflowingHorizontally =
      tableRef.current.scrollWidth > tableWrapperRef.current.clientWidth;

    setHasHorizontalOverflow(isOverflowingHorizontally);
  }, [tableRef, tableWrapperRef]);

  useEffect(() => {
    if (!tableWrapperRef.current) return;

    const isOverflowingVertically =
      tableWrapperRef.current.scrollHeight >
      tableWrapperRef.current.clientHeight;

    setHasVerticalOverflow(isOverflowingVertically);
  }, [tableWrapperRef]);

  const handleToggleExpansion = () => {
    setIsExpanded(previous => !previous);
  };

  return (
    <div
      className={classNames(styles.component, className, {
        [styles.hasHorizontalOverflow]: hasHorizontalOverflow,
        [styles.hasVerticalOverflow]: hasVerticalOverflow,
        [styles.isExpanded]: isExpanded,
      })}
    >
      <div ref={tableWrapperRef} className={styles.tableWrapper}>
        <table ref={tableRef} cellSpacing={0} className={styles.table}>
          {headlines.length > 0 && (
            <thead>
              <tr className={classNames(styles.tableRow, styles.isHead)}>
                {headlines.map((headline, headlineIndex) => (
                  <th
                    key={headlineIndex}
                    className={classNames(styles.tableRowItem, {
                      [styles.isHeadline]: headlineIndex === 0,
                    })}
                  >
                    {headline &&
                      (headlineIndex === 0 ? (
                        <Typography
                          className={styles.headline}
                          tagStyle="bodyMediumBold"
                        >
                          {headline}
                        </Typography>
                      ) : (
                        <React.Fragment>
                          <Typography tag="p" tagStyle="date">
                            {headline}
                          </Typography>

                          <Typography tag="p" tagStyle="bodySmall">
                            {t('items.common.words.days')}
                          </Typography>
                        </React.Fragment>
                      ))}
                  </th>
                ))}
              </tr>
            </thead>
          )}

          {items.length > 0 && (
            <tbody>
              {items.map((item, itemIndex) => (
                <tr key={itemIndex} className={styles.tableRow}>
                  {item.map((tableItem, tableItemIndex) => (
                    <td
                      key={tableItemIndex}
                      className={classNames(styles.tableRowItem, {
                        [styles.isHeadline]: tableItemIndex === 0,
                      })}
                    >
                      <Typography tagStyle="bodySmall">
                        {typeof tableItem === 'number' && showPricePerPerson
                          ? `${t('items.common.words.pricePerPerson')} ${currencyFormatter(tableItem)}`
                          : tableItem}
                      </Typography>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {hasVerticalOverflow && (
        <button onClick={handleToggleExpansion}>
          <Typography className={styles.viewMoreText} tagStyle="bodyMedium">
            {isExpanded
              ? `- ${t('items.common.words.showLess')}`
              : `+ ${t('items.common.words.showMore')}`}
          </Typography>
        </button>
      )}
    </div>
  );
};

AddOnProductsMatrix.displayName = 'AddOnProductsMatrix';

export default AddOnProductsMatrix;
